import React, { useState, useEffect } from 'react';
import ORB_ASSET from '../orbAsset';

const ORB_URL = ORB_ASSET;
const WORDMARK_URL = "/hypseya-wordmark.png";

// Matrescence warm palette
const MAT_ACTIVE = 'rgba(196,134,106,0.95)';
const MAT_ACTIVE_BG = 'rgba(180,100,70,0.18)';
const MAT_BORDER = 'rgba(196,134,106,0.42)';

const ROLES = [
  // ── Personal ──────────────────────────────────────────────────────────────
  {
    id: 'user',
    icon: (active) => (
      <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
        <circle cx="8.5" cy="5.5" r="3" stroke={active ? 'rgba(30,220,210,0.95)' : 'rgba(255,255,255,0.45)'} strokeWidth="1.4"/>
        <path d="M2 15c0-3.3 2.9-6 6.5-6s6.5 2.7 6.5 6" stroke={active ? 'rgba(30,220,210,0.95)' : 'rgba(255,255,255,0.45)'} strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    title: 'User / Patient',
    desc: 'Daily emotional growth and integration',
  },
  {
    id: 'matrescence',
    type: 'mode',  // mode, not role — user role stays 'user', mode = 'matrescence'
    icon: (active) => (
      <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
        <circle cx="8.5" cy="9.5" r="6" stroke={active ? MAT_ACTIVE : 'rgba(255,255,255,0.45)'} strokeWidth="1.4"/>
        <circle cx="8.5" cy="9.5" r="2.2" fill={active ? MAT_ACTIVE : 'rgba(255,255,255,0.28)'} opacity="0.9"/>
        <path d="M7 3.5 Q8.5 1.5 10 3.5" stroke={active ? MAT_ACTIVE : 'rgba(255,255,255,0.45)'} strokeWidth="1.3" strokeLinecap="round" fill="none"/>
      </svg>
    ),
    title: 'Pregnant or new mother',
    desc: 'EPDS check-ins, somatic practices, matrescence support',
    warm: true,
  },
  // ── Clinical professionals ─────────────────────────────────────────────────
  {
    id: 'therapist',
    clinical: true,  // triggers clinical group separator
    icon: (active) => (
      <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
        <circle cx="8.5" cy="8.5" r="6.5" stroke={active ? 'rgba(30,220,210,0.95)' : 'rgba(255,255,255,0.45)'} strokeWidth="1.4"/>
        <circle cx="8.5" cy="8.5" r="2.8" stroke={active ? 'rgba(30,220,210,0.95)' : 'rgba(255,255,255,0.45)'} strokeWidth="1.4"/>
      </svg>
    ),
    title: 'Therapist / Facilitator',
    desc: 'Monitor patients, track outcomes, access clinical data',
  },
  {
    id: 'doctor',
    icon: (active) => (
      <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
        <rect x="1.5" y="1.5" width="14" height="14" rx="3.5" stroke={active ? 'rgba(30,220,210,0.95)' : 'rgba(255,255,255,0.45)'} strokeWidth="1.4"/>
        <path d="M8.5 5V12M5 8.5H12" stroke={active ? 'rgba(30,220,210,0.95)' : 'rgba(255,255,255,0.45)'} strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Doctor / Psychiatrist',
    desc: 'Clinical monitoring and longitudinal patient data',
  },
  {
    id: 'institution',
    icon: (active) => (
      <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
        <rect x="1.5" y="8" width="14" height="7.5" rx="2" stroke={active ? 'rgba(30,220,210,0.95)' : 'rgba(255,255,255,0.45)'} strokeWidth="1.4"/>
        <path d="M8.5 1.5L1.5 8H15.5L8.5 1.5Z" stroke={active ? 'rgba(30,220,210,0.95)' : 'rgba(255,255,255,0.45)'} strokeWidth="1.4" strokeLinejoin="round"/>
        <path d="M5.5 11.5V15.5M11.5 11.5V15.5" stroke={active ? 'rgba(30,220,210,0.95)' : 'rgba(255,255,255,0.45)'} strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Clinic / University',
    desc: 'Group protocols, research access and institutional tools',
  },
];

// Cinematic text phases — auto-advance unless duration is null
const PHASES = [
  { id: 0, duration: 1200 },  // Orb breathes in silence
  { id: 1, text: "There is something in your nervous system\nthat no app has ever truly listened to.", duration: 4000 },
  { id: 2, text: "HYPSEYA was built for that.", duration: 3000 },
  { id: 3, text: "This is not meditation. Not wellness.", sub: "It is emotional technology — for those who\ntake their nervous system seriously.", duration: 4200 },
  { id: 4, text: "For patients in process.\nTherapists. Doctors. Clinics.", duration: 3800 },
  { id: 5, duration: null },  // Role selection — waits for user
];

export default function IntroExperience({ onComplete }) {
  const [phase, setPhase] = useState(0);
  const [textVisible, setTextVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showCTA, setShowCTA] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [orbPowered, setOrbPowered] = useState(false);

  // Auto-advance phases
  useEffect(() => {
    const current = PHASES[phase];
    if (!current || current.duration === null) return;

    const showTimer = setTimeout(() => setTextVisible(true), 180);
    const advTimer = setTimeout(() => {
      setTextVisible(false);
      setTimeout(() => {
        setPhase(p => Math.min(p + 1, PHASES.length - 1));
      }, 650);
    }, current.duration);

    return () => { clearTimeout(showTimer); clearTimeout(advTimer); };
  }, [phase]);

  // Orb intensity increases through phases
  useEffect(() => {
    if (phase >= 2) setOrbPowered(true);
    if (phase >= 5) setOrbPowered(false);
  }, [phase]);

  // Reveal CTA after role chosen
  useEffect(() => {
    if (!selectedRole) return;
    const t = setTimeout(() => setShowCTA(true), 380);
    return () => clearTimeout(t);
  }, [selectedRole]);

  const handleComplete = (action) => {
    localStorage.setItem('hypseya_intro_seen', 'true');
    if (selectedRole === 'matrescence') {
      // Matrescence is a mode, not a clinical role
      localStorage.setItem('hypseya_user_mode', 'matrescence');
      localStorage.removeItem('hypseya_user_role');
    } else if (selectedRole) {
      localStorage.setItem('hypseya_user_role', selectedRole);
      localStorage.removeItem('hypseya_user_mode');
    }
    setExiting(true);
    setTimeout(() => onComplete(action, selectedRole || 'user'), 750);
  };

  const isRolePhase = phase >= 5;
  const current = PHASES[Math.min(phase, PHASES.length - 1)];

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'radial-gradient(ellipse at 50% 38%, #0d2220 0%, #071714 45%, #030c0b 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      opacity: exiting ? 0 : 1,
      transition: 'opacity 0.85s ease',
      overflow: 'hidden',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      padding: '24px 20px',
    }}>

      {/* Logo block — orb on top, wordmark below, absolutely centered */}
      <div style={{
        position: 'absolute',
        top: isRolePhase ? '8%' : '6%',
        left: '50%',
        transform: 'translate(-50%, 0)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        transition: 'transform 1.2s ease',
        zIndex: 1,
        pointerEvents: 'none',
      }}>
        {/* Orb */}
        <div style={{
          position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: isRolePhase ? 12 : 20,
          transition: 'margin 1.2s ease',
        }}>
          <div style={{
            position: 'absolute',
            width: isRolePhase ? 'min(110px, 26vw)' : 'min(290px, 68vw)',
            height: isRolePhase ? 'min(110px, 26vw)' : 'min(290px, 68vw)',
            borderRadius: '50%',
            background: 'radial-gradient(ellipse at center, rgba(45,139,135,0.22) 0%, rgba(30,107,104,0.08) 55%, transparent 80%)',
            transition: 'width 1.2s ease, height 1.2s ease',
          }} />
          <img
            src={ORB_URL}
            alt=""
            style={{
              width: isRolePhase ? 'min(100px, 24vw)' : 'min(280px, 66vw)',
              height: 'auto',
              opacity: isRolePhase ? 0.6 : 1,
              animation: 'hyp-heartbeat 5s ease-in-out infinite',
              transition: 'width 1.2s ease, opacity 1.2s ease',
              filter: 'drop-shadow(0 4px 24px rgba(20,80,78,0.35))',
              position: 'relative',
            }}
          />
        </div>

        {/* Wordmark — visible only in phase 0 */}
        <img
          src={WORDMARK_URL}
          alt="HYPSEYA"
          style={{
            width: 'min(300px, 72vw)',
            height: 'auto',
            opacity: phase === 0 ? 1 : 0,
            filter: 'drop-shadow(0 2px 16px rgba(30,200,190,0.3))',
            transition: 'opacity 0.8s ease',
            pointerEvents: 'none',
          }}
        />
        {/* Subtle glow line below wordmark in cinematic phases */}
        {!isRolePhase && (
          <div style={{
            marginTop: 14,
            width: 'min(80px, 20vw)',
            height: 1,
            background: 'linear-gradient(90deg, transparent, rgba(30,200,190,0.4), transparent)',
            animation: 'hyp-orb-pulse 3s ease-in-out infinite',
          }} />
        )}
      </div>

      {/* Cinematic text phases */}
      {!isRolePhase && phase > 0 && (
        <div style={{
          position: 'absolute',
          bottom: '18%',
          left: '50%',
          transform: textVisible ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(16px)',
          textAlign: 'center',
          width: '88vw',
          maxWidth: 360,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 8px',
          opacity: textVisible ? 1 : 0,
          transition: 'opacity 0.75s ease, transform 0.75s ease',
        }}>
          {current?.text && (
            <p style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(1.05rem, 4.2vw, 1.32rem)',
              color: 'rgba(255,255,255,0.88)',
              lineHeight: 1.62,
              margin: '0 0 14px',
              fontWeight: 400,
              whiteSpace: 'pre-line',
              letterSpacing: '0.01em',
            }}>
              {current.text}
            </p>
          )}
          {current?.sub && (
            <p style={{
              fontSize: '0.87rem',
              color: 'rgba(30,200,190,0.62)',
              lineHeight: 1.7,
              margin: 0,
              whiteSpace: 'pre-line',
              letterSpacing: '0.01em',
            }}>
              {current.sub}
            </p>
          )}
        </div>
      )}

      {/* ── Role selection ── */}
      {isRolePhase && (
        <div style={{
          width: '100%',
          maxWidth: 430,
          display: 'flex',
          flexDirection: 'column',
          gap: 9,
          animation: 'hyp-fade-up 0.75s ease forwards',
          marginTop: 'min(140px, 18vh)',
        }}>
          <p style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(1rem, 3.8vw, 1.22rem)',
            color: 'rgba(255,255,255,0.82)',
            textAlign: 'center',
            margin: '0 0 14px',
            fontStyle: 'italic',
            letterSpacing: '0.015em',
          }}>
            Who are you here?
          </p>

          {ROLES.map((role, idx) => {
            const active = selectedRole === role.id;
            const isWarm = role.warm;
            // Color tokens — teal for regular roles, warm terracota for matrescence
            const activeText  = isWarm ? MAT_ACTIVE      : 'rgba(30,220,210,0.95)';
            const activeBg    = isWarm ? MAT_ACTIVE_BG   : 'rgba(30,107,104,0.22)';
            const activeBorder= isWarm ? MAT_BORDER      : 'rgba(30,200,190,0.48)';
            const iconRingBg  = isWarm ? 'rgba(180,100,70,0.28)' : 'rgba(30,107,104,0.38)';
            const iconRingBorder = isWarm ? 'rgba(196,134,106,0.38)' : 'rgba(30,200,190,0.42)';
            const dotBg       = isWarm ? 'rgba(196,134,106,0.9)'  : 'rgba(30,210,200,0.9)';
            const checkBorder = isWarm ? 'rgba(196,134,106,0.75)' : 'rgba(30,200,190,0.75)';
            const checkBg     = isWarm ? 'rgba(180,100,70,0.28)'  : 'rgba(30,107,104,0.35)';

            return (
              <React.Fragment key={role.id}>
                {/* Separator before clinical group */}
                {role.clinical && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    margin: '4px 0 2px',
                  }}>
                    <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
                    <span style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.25)', letterSpacing: '0.07em', textTransform: 'uppercase' }}>Clinical professionals</span>
                    <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
                  </div>
                )}
                <button
                  onClick={() => setSelectedRole(role.id)}
                  style={{
                    width: '100%',
                    padding: '11px 16px',
                    background: active ? activeBg : isWarm ? 'rgba(196,134,106,0.055)' : 'rgba(255,255,255,0.034)',
                    border: `1.5px solid ${active ? activeBorder : isWarm ? 'rgba(196,134,106,0.18)' : 'rgba(255,255,255,0.085)'}`,
                    borderRadius: 14,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 13,
                    textAlign: 'left',
                    transition: 'all 0.25s ease',
                    outline: 'none',
                  }}
                >
                  {/* Icon bubble */}
                  <div style={{
                    width: 34, height: 34,
                    borderRadius: '50%',
                    background: active ? iconRingBg : 'rgba(255,255,255,0.06)',
                    border: `1px solid ${active ? iconRingBorder : 'rgba(255,255,255,0.1)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.25s ease',
                  }}>
                    {role.icon(active)}
                  </div>

                  {/* Text */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      margin: 0,
                      color: active ? activeText : 'rgba(255,255,255,0.78)',
                      fontWeight: 600,
                      fontSize: '0.91rem',
                      transition: 'color 0.25s',
                      lineHeight: 1.3,
                    }}>
                      {role.title}
                    </p>
                    <p style={{
                      margin: '3px 0 0',
                      color: 'rgba(255,255,255,0.32)',
                      fontSize: '0.74rem',
                      lineHeight: 1.45,
                    }}>
                      {role.desc}
                    </p>
                  </div>

                  {/* Selected indicator */}
                  <div style={{
                    width: 18, height: 18,
                    borderRadius: '50%',
                    border: `1.5px solid ${active ? checkBorder : 'rgba(255,255,255,0.12)'}`,
                    background: active ? checkBg : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.25s ease',
                  }}>
                    {active && (
                      <div style={{
                        width: 8, height: 8,
                        borderRadius: '50%',
                        background: dotBg,
                      }} />
                    )}
                  </div>
                </button>
              </React.Fragment>
            );
          })}

          {/* CTAs — appear after role selection */}
          <div style={{
            marginTop: 8,
            opacity: showCTA ? 1 : 0,
            transform: showCTA ? 'translateY(0)' : 'translateY(12px)',
            transition: 'opacity 0.5s ease, transform 0.5s ease',
            pointerEvents: showCTA ? 'auto' : 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}>
            <button
              onClick={() => handleComplete('signup')}
              style={{
                width: '100%',
                padding: '15px',
                background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: 26,
                fontSize: '0.95rem',
                fontWeight: 600,
                cursor: 'pointer',
                letterSpacing: '0.025em',
                boxShadow: '0 6px 28px rgba(30,107,104,0.45)',
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              Enter the portal →
            </button>
            <button
              onClick={() => handleComplete('login')}
              style={{
                width: '100%',
                padding: '12px',
                background: 'transparent',
                color: 'rgba(255,255,255,0.38)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 26,
                fontSize: '0.82rem',
                cursor: 'pointer',
                letterSpacing: '0.02em',
                transition: 'color 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.65)'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.38)'}
            >
              I already have an account
            </button>
          </div>
        </div>
      )}

      {/* Phase progress */}
      {!isRolePhase && phase > 0 && (
        <div style={{
          position: 'absolute', bottom: 30,
          display: 'flex', gap: 7,
          pointerEvents: 'none',
        }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{
              height: 4,
              width: phase > i ? 20 : (phase === i ? 12 : 5),
              borderRadius: 2,
              background: phase > i
                ? 'rgba(30,210,200,0.65)'
                : (phase === i ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.12)'),
              transition: 'all 0.4s ease',
            }} />
          ))}
        </div>
      )}

      <style>{`
        @keyframes hyp-heartbeat {
          0%   { transform: scale(1);    opacity: 0.92; }
          50%  { transform: scale(1.05); opacity: 1; }
          100% { transform: scale(1);    opacity: 0.92; }
        }
        @keyframes hyp-orb-pulse {
          0%, 100% { opacity: 0.5; transform: scale(0.96); }
          50% { opacity: 1; transform: scale(1.04); }
        }
        @keyframes hyp-fade-up {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
